// Dockable simulation panel. Renders three columns:
//   1. Scenario templates (list + new/dup/delete, checkboxes, max-5 run)
//   2. Active scenario editor (inputs, live re-eval)
//   3. Result cards (up to 5 side-by-side, expandable, breadcrumb on failure)
//
// Templates live in localStorage keyed by the host-provided storageKey. The
// panel observes the host EpxDmn instance for edit events and re-evaluates
// the active scenario automatically (debounced).

import './simulationStyles.css';
import { DmnSimulator } from './DmnSimulator';
import { evaluate as feelEvaluate } from '../feel/Evaluator';
import { stringify } from '../feel/builtins';

const MAX_BATCH = 5;
const LIVE_EVAL_DEBOUNCE_MS = 220;

function uid() {
  return 'sc_' + Math.random().toString(36).slice(2, 10);
}

function safeParse(s, fallback) {
  try { return JSON.parse(s); } catch { return fallback; }
}

function defaultScenario(name = 'Escenario') {
  return { id: uid(), name, inputs: {}, expected: {} };
}

function decisionShape(el) {
  const logic = el && el.raw && el.raw.decisionLogic;
  if (!logic) return { kind: 'feel' };
  if (logic.$type !== 'dmn:DecisionTable') return { kind: 'feel' };
  const outs = logic.output || [];
  const hp = logic.hitPolicy || 'UNIQUE';
  const listy = hp === 'COLLECT' || hp === 'RULE ORDER' || hp === 'OUTPUT ORDER';
  if (listy || outs.length === 0) return { kind: 'feel' };
  if (outs.length === 1) return { kind: 'scalar', typeRef: outs[0].typeRef || 'string' };
  return {
    kind: 'object',
    columns: outs.map((o, i) => ({
      name: o.name || `output_${i + 1}`,
      typeRef: o.typeRef || 'string',
    })),
  };
}

function deepEqual(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => deepEqual(v, b[i]));
  }
  if (typeof a === 'object') {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every((k) => deepEqual(a[k], b[k]));
  }
  return false;
}

export class SimulationPanel {
  constructor(container, {
    epxDmn,
    storageKey = 'epx-dmn.simulation.default',
    onRevealCell = null,
    onClose = null,
  } = {}) {
    if (!container) throw new Error('[SimulationPanel] container required');
    if (!epxDmn) throw new Error('[SimulationPanel] epxDmn is required');

    this._container = container;
    this._epx = epxDmn;
    this._storageKey = storageKey;
    this._onRevealCell = typeof onRevealCell === 'function' ? onRevealCell : null;
    this._onClose = typeof onClose === 'function' ? onClose : null;

    this._scenarios = this._loadScenarios();
    if (!this._scenarios.length) {
      this._scenarios.push(defaultScenario('baseline'));
    }
    this._activeId = this._scenarios[0].id;
    this._selectedIds = new Set([this._activeId]);
    this._resultsById = new Map();
    this._expandedIds = new Set();
    this._liveEvalTimer = null;
    this._redrawTimer = null;
    this._liveEvalOn = true;
    this._focusedDecisionId = null;

    this._container.classList.add('epx-dmn-sim');

    // Always refresh panel UI (input fields, expected section, decision shape)
    // when the model changes. Auto-re-eval is gated on at least one prior run
    // so opening the panel on a fresh diagram doesn't paint pass/fail rings on
    // the DRD without an explicit action.
    this._onModelEdit = () => {
      this._scheduleRedraw();
      if (this._resultsById.size === 0) return;
      this._scheduleLiveEval();
    };
    this._epx.on('decisionTable.changed', this._onModelEdit);
    this._epx.on('expression.changed', this._onModelEdit);
    this._epx.on('element.changed', this._onModelEdit);
    this._epx.on('import.done', this._onModelEdit);

    this._onViewChanged = ({ view }) => {
      if (view === 'drd') {
        // DRD was just mounted — re-apply sim status to the freshly created nodes.
        setTimeout(() => this._paintDrd(), 30);
      }
    };
    this._epx.on('view.changed', this._onViewChanged);

    this._onElementClick = ({ element }) => {
      if (!element || !element.id) {
        if (this._focusedDecisionId) {
          this._focusedDecisionId = null;
          this._renderResults();
        }
        return;
      }
      const model = this._getModel();
      if (!model) return;
      const match = (model.drgElements || []).find((e) => e.id === element.id && e.kind === 'decision');
      const next = match ? match.id : null;
      if (next !== this._focusedDecisionId) {
        this._focusedDecisionId = next;
        this._renderResults();
      }
    };
    this._epx.on('element.click', this._onElementClick);

    // No auto-eval on mount: colors only show after the user runs something
    // explicitly (input change, expected change, or Run button).
    this._redraw();
  }

  setStorageKey(newKey) {
    if (newKey === this._storageKey) return;
    this._storageKey = newKey;
    this._scenarios = this._loadScenarios();
    if (!this._scenarios.length) {
      this._scenarios.push(defaultScenario('baseline'));
    }
    this._activeId = this._scenarios[0].id;
    this._selectedIds = new Set([this._activeId]);
    this._resultsById = new Map();
    this._expandedIds = new Set();
    this._clearDrd();
    this._redraw();
  }

  destroy() {
    if (this._onModelEdit) {
      this._epx.off('decisionTable.changed', this._onModelEdit);
      this._epx.off('expression.changed', this._onModelEdit);
      this._epx.off('element.changed', this._onModelEdit);
      this._epx.off('import.done', this._onModelEdit);
    }
    if (this._onViewChanged) this._epx.off('view.changed', this._onViewChanged);
    if (this._onElementClick) this._epx.off('element.click', this._onElementClick);
    if (this._liveEvalTimer) {
      clearTimeout(this._liveEvalTimer);
      this._liveEvalTimer = null;
    }
    if (this._redrawTimer) {
      clearTimeout(this._redrawTimer);
      this._redrawTimer = null;
    }
    this._clearTableHighlights();
    this._clearDrd();
    this._container.classList.remove('epx-dmn-sim');
    this._container.innerHTML = '';
  }

  // ── Public API (PR 1 §4.1) ──────────────────────────────────────────
  //
  // Programmatic scenario API used by the Atlas chat (and the wizard).
  // The methods accept the same shape that's persisted in localStorage so a
  // round-trip through `getScenarios()` → `loadScenarios()` is lossless.

  /**
   * Replace the stored scenarios with the supplied list. Each scenario may
   * provide `id`, `name`, `inputs` (object) and `expected` (object); missing
   * fields are filled with sensible defaults. Returns the number of scenarios
   * actually loaded (after filtering invalid entries).
   */
  loadScenarios(scenarios = []) {
    if (!Array.isArray(scenarios)) return 0;
    const cleaned = scenarios
      .filter((s) => s && typeof s === 'object')
      .map((s) => ({
        id: s.id || uid(),
        name: String(s.name || 'Escenario'),
        inputs: s.inputs && typeof s.inputs === 'object' ? { ...s.inputs } : {},
        expected: s.expected && typeof s.expected === 'object' ? { ...s.expected } : {},
      }));
    if (!cleaned.length) cleaned.push(defaultScenario('baseline'));
    this._scenarios = cleaned;
    this._activeId = this._scenarios[0].id;
    this._selectedIds = new Set([this._activeId]);
    this._resultsById = new Map();
    this._expandedIds = new Set();
    this._saveScenarios();
    this._clearDrd();
    this._redraw();
    return cleaned.length;
  }

  /**
   * Evaluate every stored scenario synchronously and return an array of
   * `{ id, name, ok, result, assertions, message }` rows in scenario order.
   * Side-effect: the panel refreshes the active scenario's highlights so the
   * user can pick up where the script left off.
   */
  runAll() {
    if (!Array.isArray(this._scenarios) || !this._scenarios.length) return [];
    this._resultsById.clear();
    const out = [];
    for (const sc of this._scenarios) {
      const res = this._runScenario(sc);
      this._resultsById.set(sc.id, res);
      out.push({ id: sc.id, name: sc.name, ...res });
    }
    if (this._activeId) {
      this._renderResults();
      this._applyTableHighlightsFromActive();
      this._paintDrd();
    }
    return out;
  }

  /**
   * Snapshot of the most recent run, keyed by scenario id. Empty if `runAll`
   * hasn't been called yet.
   */
  getResults() {
    const out = [];
    for (const sc of this._scenarios) {
      const r = this._resultsById.get(sc.id);
      if (!r) continue;
      out.push({ id: sc.id, name: sc.name, ...r });
    }
    return out;
  }

  /**
   * Snapshot of the persisted scenario list — same shape as `loadScenarios`
   * accepts so the round-trip is lossless.
   */
  getScenarios() {
    return (this._scenarios || []).map((s) => ({
      id: s.id,
      name: s.name,
      inputs: { ...(s.inputs || {}) },
      expected: { ...(s.expected || {}) },
    }));
  }

  // ── Persistence ──────────────────────────────────────────────────────

  _loadScenarios() {
    if (typeof localStorage === 'undefined') return [];
    const raw = localStorage.getItem(this._storageKey);
    if (!raw) return [];
    const arr = safeParse(raw, []);
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((s) => s && typeof s === 'object')
      .map((s) => ({
        id: s.id || uid(),
        name: String(s.name || 'Escenario'),
        inputs: s.inputs && typeof s.inputs === 'object' ? { ...s.inputs } : {},
        expected: s.expected && typeof s.expected === 'object' ? { ...s.expected } : {},
      }));
  }

  _saveScenarios() {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(this._storageKey, JSON.stringify(this._scenarios));
    } catch (err) {
      console.warn('[SimulationPanel] save failed:', err);
    }
  }

  // ── Model introspection ─────────────────────────────────────────────

  _getModel() { return this._epx.getModel() || null; }

  _inputDataElements() {
    const m = this._getModel();
    if (!m) return [];
    return (m.drgElements || []).filter((e) => e.kind === 'inputData');
  }

  _decisionElements() {
    const m = this._getModel();
    if (!m) return [];
    return (m.drgElements || []).filter((e) => e.kind === 'decision');
  }

  _inputKey(el) {
    const v = el.raw && el.raw.variable;
    return (v && v.name) || el.name || el.id;
  }

  _inputTypeRef(el) {
    const v = el.raw && el.raw.variable;
    return (v && v.typeRef) || 'string';
  }

  _coerce(value, typeRef) {
    if (value === '' || value == null) return undefined;
    const v = String(value).trim();
    if (typeRef === 'number' || typeRef === 'integer' || typeRef === 'double') {
      const n = Number(v);
      return Number.isNaN(n) ? null : n;
    }
    if (typeRef === 'boolean') {
      if (v === 'true') return true;
      if (v === 'false') return false;
      return null;
    }
    if (v.length >= 2 && v.startsWith('"') && v.endsWith('"')) return v.slice(1, -1);
    return v;
  }

  // ── Evaluation ──────────────────────────────────────────────────────

  _scheduleLiveEval(delay = LIVE_EVAL_DEBOUNCE_MS) {
    if (!this._liveEvalOn) return;
    if (this._liveEvalTimer) clearTimeout(this._liveEvalTimer);
    this._liveEvalTimer = setTimeout(() => {
      this._liveEvalTimer = null;
      this._runSelected({ silent: true });
    }, delay);
  }

  _scheduleRedraw() {
    if (this._redrawTimer) return;
    this._redrawTimer = setTimeout(() => {
      this._redrawTimer = null;
      this._redraw();
    }, 0);
  }

  _buildTypedInputs(scenario) {
    const typed = {};
    this._inputDataElements().forEach((el) => {
      const key = this._inputKey(el);
      const raw = scenario.inputs[key];
      if (raw === '' || raw == null) return;
      typed[key] = this._coerce(raw, this._inputTypeRef(el));
    });
    return typed;
  }

  _runScenario(scenario) {
    const model = this._getModel();
    if (!model) return { ok: false, message: 'No hay modelo cargado.' };
    const typed = this._buildTypedInputs(scenario);
    try {
      const sim = new DmnSimulator({ model });
      const res = sim.evaluate(typed);
      const assertions = this._assertExpected(scenario, res);
      return { ok: true, result: res, assertions };
    } catch (err) {
      return { ok: false, message: err && err.message || String(err) };
    }
  }

  /**
   * Compare actual results against scenario.expected (if any set).
   * Returns { perDecision: { [id]: { status, expected, actual, parseError } }, pass, fail, total }.
   *   status: 'pass' | 'fail' | 'error' | 'none'
   */
  _assertExpected(scenario, res) {
    const out = { perDecision: {}, pass: 0, fail: 0, errors: 0, total: 0 };
    if (!scenario.expected) return out;
    const decs = this._decisionElements();
    decs.forEach((el) => {
      const stored = scenario.expected[el.id];
      if (!stored) return;
      if (!this._hasExpectedSet(scenario, el)) {
        out.perDecision[el.id] = { status: 'none' };
        return;
      }
      const shape = decisionShape(el);
      const parsed = this._parseExpected(stored, shape);
      const actual = res.results[el.id];
      if (parsed.parseError) {
        out.total += 1;
        out.errors += 1;
        out.perDecision[el.id] = { status: 'error', parseError: parsed.parseError, actual };
        return;
      }
      const ok = deepEqual(actual, parsed.value);
      out.total += 1;
      if (ok) { out.pass += 1; }
      else { out.fail += 1; }
      out.perDecision[el.id] = {
        status: ok ? 'pass' : 'fail',
        expected: parsed.value,
        actual,
      };
    });
    return out;
  }

  _parseExpected(stored, shape) {
    if (!stored) return { value: null };
    if (stored.mode === 'scalar') {
      const typeRef = shape.kind === 'scalar' ? shape.typeRef : 'string';
      return { value: this._coerce(stored.value, typeRef) };
    }
    if (stored.mode === 'object') {
      const vals = stored.values || {};
      const cols = shape.kind === 'object' ? shape.columns : [];
      const obj = {};
      cols.forEach((c) => {
        const raw = vals[c.name];
        if (raw === '' || raw == null) return;
        obj[c.name] = this._coerce(raw, c.typeRef);
      });
      return { value: obj };
    }
    if (stored.mode === 'feel') {
      const text = stored.text || '';
      if (!text.trim()) return { value: null };
      try {
        return { value: feelEvaluate(text, {}) };
      } catch (err) {
        return { parseError: err && err.message || String(err) };
      }
    }
    return { value: null };
  }

  _runSelected({ silent } = {}) {
    const ids = Array.from(this._selectedIds).slice(0, MAX_BATCH);
    if (!ids.length) {
      if (this._activeId) ids.push(this._activeId);
    }
    this._resultsById.clear();
    ids.forEach((id) => {
      const scenario = this._scenarios.find((s) => s.id === id);
      if (!scenario) return;
      this._resultsById.set(id, this._runScenario(scenario));
    });
    this._renderResults();
    this._applyTableHighlightsFromActive();
    this._paintDrd();
    if (!silent) { /* nothing extra for now */ }
  }

  _paintDrd() {
    if (!this._epx || typeof this._epx.setNodeSimStatus !== 'function') return;
    // Only paint when we're in DRD view. In other views this is a no-op.
    if (typeof this._epx.getView === 'function' && this._epx.getView() !== 'drd') return;

    const model = this._getModel();
    if (!model) return;

    const activeRes = this._resultsById.get(this._activeId);
    if (!activeRes) {
      this._clearDrd();
      return;
    }

    const trace = (activeRes.ok && activeRes.result && activeRes.result.trace) || [];
    const byId = new Map(trace.map((t) => [t.id, t]));
    const perDec = (activeRes.assertions && activeRes.assertions.perDecision) || {};

    (model.drgElements || []).forEach((el) => {
      const entry = byId.get(el.id);
      let status;
      if (!entry) {
        // Knowledge sources aren't in the trace, but they're also not "run" in
        // the execution sense. Treat as skipped only for Decision/BKM/InputData.
        status = (el.kind === 'knowledgeSource') ? null : 'skipped';
      } else if (entry.error) {
        status = 'error';
      } else if (el.kind === 'decision') {
        const assertion = perDec[el.id];
        if (assertion && assertion.status === 'pass') status = 'pass';
        else if (assertion && (assertion.status === 'fail' || assertion.status === 'error')) status = 'fail';
        else status = 'ok';
      } else {
        status = 'ok';
      }
      this._epx.setNodeSimStatus(el.id, status);
    });
  }

  _clearDrd() {
    if (this._epx && typeof this._epx.clearAllSimStatus === 'function') {
      this._epx.clearAllSimStatus();
    }
  }

  _applyTableHighlightsFromActive() {
    const res = this._resultsById.get(this._activeId);
    if (!res) return this._clearTableHighlights();

    const editor = this._epx.getActiveEditor && this._epx.getActiveEditor();
    if (!editor || typeof editor.setSimulationHighlights !== 'function') return;

    const tableDecision = editor.getDecision && editor.getDecision();
    if (!tableDecision) {
      editor.clearSimulationHighlights && editor.clearSimulationHighlights();
      return;
    }

    const trace = (res.ok && res.result && res.result.trace) || [];
    const entry = trace.find((t) => t.id === tableDecision.id && t.kind === 'decision');
    if (!entry) {
      editor.clearSimulationHighlights && editor.clearSimulationHighlights();
      return;
    }

    if (entry.error) {
      const ctx = entry.errorContext || {};
      if (ctx.kind === 'hitPolicy' && Array.isArray(ctx.conflictRuleIdxs)) {
        editor.setSimulationHighlights({ conflict: ctx.conflictRuleIdxs });
      } else if (typeof ctx.ruleIdx === 'number') {
        const errKind = ctx.kind === 'outputEntry' ? 'outputEntry' : 'inputEntry';
        editor.setSimulationHighlights({
          error: { ruleId: ctx.ruleId || ctx.ruleIdx, colIdx: ctx.inputIdx ?? ctx.outputIdx, kind: errKind },
        });
      } else {
        editor.clearSimulationHighlights && editor.clearSimulationHighlights();
      }
      return;
    }
    if (Array.isArray(entry.matchedRules) && entry.matchedRules.length) {
      editor.setSimulationHighlights({ matched: entry.matchedRules });
    } else {
      editor.clearSimulationHighlights && editor.clearSimulationHighlights();
    }
  }

  _clearTableHighlights() {
    const editor = this._epx.getActiveEditor && this._epx.getActiveEditor();
    if (editor && typeof editor.clearSimulationHighlights === 'function') {
      editor.clearSimulationHighlights();
    }
  }

  // ── UI: shell ───────────────────────────────────────────────────────

  _redraw() {
    this._container.innerHTML = '';
    const root = document.createElement('div');
    root.className = 'epx-dmn-sim__root';
    root.appendChild(this._buildTemplatesColumn());
    root.appendChild(this._buildActiveColumn());
    root.appendChild(this._buildResultsColumn());
    this._container.appendChild(root);

    if (this._onClose) {
      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'epx-dmn-sim__close';
      closeBtn.title = 'Cerrar simulador';
      closeBtn.setAttribute('aria-label', 'Cerrar simulador');
      closeBtn.textContent = '✕';
      closeBtn.addEventListener('click', () => {
        try { this._onClose(); } catch (err) { console.warn('[SimulationPanel] onClose:', err); }
      });
      this._container.appendChild(closeBtn);
    }

    this._renderResults();
  }

  // ── UI: templates column ────────────────────────────────────────────

  _buildTemplatesColumn() {
    const col = document.createElement('div');
    col.className = 'epx-dmn-sim__col epx-dmn-sim__col-templates';

    const head = document.createElement('div');
    head.className = 'epx-dmn-sim__col-head';
    head.textContent = 'Escenarios';
    col.appendChild(head);

    const list = document.createElement('div');
    list.className = 'epx-dmn-sim__templates';
    this._scenarios.forEach((sc) => list.appendChild(this._buildTemplateRow(sc)));
    col.appendChild(list);

    const toolbar = document.createElement('div');
    toolbar.className = 'epx-dmn-sim__tpl-toolbar';

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'epx-dmn-sim__btn epx-dmn-sim__btn--ghost';
    addBtn.textContent = '+ Nuevo';
    addBtn.addEventListener('click', () => this._newScenario());
    toolbar.appendChild(addBtn);

    const dupBtn = document.createElement('button');
    dupBtn.type = 'button';
    dupBtn.className = 'epx-dmn-sim__btn epx-dmn-sim__btn--ghost';
    dupBtn.textContent = 'Duplicar';
    dupBtn.title = 'Duplicar escenario activo';
    dupBtn.addEventListener('click', () => this._duplicateActive());
    toolbar.appendChild(dupBtn);

    col.appendChild(toolbar);

    const runBar = document.createElement('div');
    runBar.className = 'epx-dmn-sim__runbar';

    const sel = this._selectedIds.size;
    const runBtn = document.createElement('button');
    runBtn.type = 'button';
    runBtn.className = 'epx-dmn-sim__btn epx-dmn-sim__btn--primary';
    runBtn.textContent = `▷ Correr (${Math.min(sel, MAX_BATCH)}/${MAX_BATCH})`;
    runBtn.disabled = sel === 0;
    if (sel > MAX_BATCH) {
      runBtn.title = `Seleccionaste ${sel}. Máximo ${MAX_BATCH} — correré los primeros ${MAX_BATCH}.`;
    }
    runBtn.addEventListener('click', () => this._runSelected({ silent: false }));
    runBar.appendChild(runBtn);

    const live = document.createElement('label');
    live.className = 'epx-dmn-sim__live';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = this._liveEvalOn;
    cb.addEventListener('change', () => {
      this._liveEvalOn = cb.checked;
      if (this._liveEvalOn) this._scheduleLiveEval(0);
    });
    live.appendChild(cb);
    live.appendChild(document.createTextNode(' Evaluación en vivo'));
    runBar.appendChild(live);

    col.appendChild(runBar);

    return col;
  }

  _buildTemplateRow(sc) {
    const row = document.createElement('div');
    row.className = 'epx-dmn-sim__tpl-row';
    if (sc.id === this._activeId) row.classList.add('is-active');

    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = this._selectedIds.has(sc.id);
    cb.title = 'Incluir en corrida batch';
    cb.addEventListener('click', (e) => e.stopPropagation());
    cb.addEventListener('change', () => {
      if (cb.checked) this._selectedIds.add(sc.id);
      else this._selectedIds.delete(sc.id);
      this._redraw();
    });
    row.appendChild(cb);

    const name = document.createElement('span');
    name.className = 'epx-dmn-sim__tpl-name';
    name.textContent = sc.name;
    name.title = 'Clic para activar · doble clic para renombrar';
    name.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'epx-dmn-sim__tpl-name epx-dmn-sim__tpl-name--editing';
      input.value = sc.name;
      const commit = (save) => {
        if (save) {
          sc.name = input.value.trim() || 'Escenario';
          this._saveScenarios();
        }
        this._redraw();
        this._renderResults();
      };
      input.addEventListener('click', (ev) => ev.stopPropagation());
      input.addEventListener('blur', () => commit(true));
      input.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter') { ev.preventDefault(); commit(true); }
        else if (ev.key === 'Escape') { ev.preventDefault(); commit(false); }
      });
      name.replaceWith(input);
      input.focus();
      input.select();
    });
    row.appendChild(name);

    const runOne = document.createElement('button');
    runOne.type = 'button';
    runOne.className = 'epx-dmn-sim__tpl-run';
    runOne.title = 'Correr solo este';
    runOne.textContent = '▷';
    runOne.addEventListener('click', (e) => {
      e.stopPropagation();
      const res = this._runScenario(sc);
      this._resultsById.set(sc.id, res);
      if (!this._selectedIds.has(sc.id)) this._selectedIds.add(sc.id);
      this._renderResults();
      this._applyTableHighlightsFromActive();
    });
    row.appendChild(runOne);

    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'epx-dmn-sim__tpl-del';
    del.title = 'Borrar escenario';
    del.textContent = '✕';
    del.addEventListener('click', (e) => {
      e.stopPropagation();
      this._deleteScenario(sc.id);
    });
    row.appendChild(del);

    row.addEventListener('click', () => this._setActive(sc.id));

    return row;
  }

  _setActive(id) {
    if (this._activeId === id) return;
    this._activeId = id;
    this._redraw();
    this._scheduleLiveEval(0);
  }

  _newScenario() {
    const sc = defaultScenario('Escenario ' + (this._scenarios.length + 1));
    this._scenarios.push(sc);
    this._activeId = sc.id;
    this._selectedIds.add(sc.id);
    this._saveScenarios();
    this._redraw();
  }

  _duplicateActive() {
    const src = this._scenarios.find((s) => s.id === this._activeId);
    if (!src) return;
    const copy = { id: uid(), name: src.name + ' (copia)', inputs: { ...src.inputs } };
    this._scenarios.push(copy);
    this._activeId = copy.id;
    this._selectedIds.add(copy.id);
    this._saveScenarios();
    this._redraw();
    this._scheduleLiveEval(0);
  }

  _deleteScenario(id) {
    const i = this._scenarios.findIndex((s) => s.id === id);
    if (i < 0) return;
    this._scenarios.splice(i, 1);
    this._selectedIds.delete(id);
    this._resultsById.delete(id);
    this._expandedIds.delete(id);
    if (this._activeId === id) {
      this._activeId = this._scenarios[0] ? this._scenarios[0].id : null;
    }
    if (!this._scenarios.length) this._scenarios.push(defaultScenario('baseline'));
    if (!this._activeId) this._activeId = this._scenarios[0].id;
    this._saveScenarios();
    this._redraw();
  }

  // ── UI: active scenario column ──────────────────────────────────────

  _buildActiveColumn() {
    const col = document.createElement('div');
    col.className = 'epx-dmn-sim__col epx-dmn-sim__col-active';

    const head = document.createElement('div');
    head.className = 'epx-dmn-sim__col-head';
    const active = this._scenarios.find((s) => s.id === this._activeId);
    head.textContent = active ? `Editando: ${active.name}` : 'Inputs';
    col.appendChild(head);

    const body = document.createElement('div');
    body.className = 'epx-dmn-sim__active-body';

    if (!active) {
      body.appendChild(this._muted('Seleccioná un escenario.'));
      col.appendChild(body);
      return col;
    }

    const inputs = this._inputDataElements();
    if (!inputs.length) {
      body.appendChild(this._muted('No hay InputData declarados en el DRG.'));
    } else {
      const inputsHdr = document.createElement('div');
      inputsHdr.className = 'epx-dmn-sim__subhead';
      inputsHdr.textContent = 'Inputs';
      body.appendChild(inputsHdr);
      inputs.forEach((el) => body.appendChild(this._buildInputRow(el, active)));
    }

    const decisions = this._decisionElements();
    if (decisions.length) {
      body.appendChild(this._buildExpectedSection(active, decisions));
    }

    col.appendChild(body);
    return col;
  }

  _buildExpectedSection(scenario, decisions) {
    const wrap = document.createElement('div');
    wrap.className = 'epx-dmn-sim__expected';

    const header = document.createElement('button');
    header.type = 'button';
    header.className = 'epx-dmn-sim__expected-head';
    const hasAny = decisions.some((el) => this._hasExpectedSet(scenario, el));
    const expanded = !!(scenario._expectedOpen || hasAny);
    scenario._expectedOpen = expanded;
    header.dataset.expanded = expanded ? '1' : '0';
    header.innerHTML = `<span class="epx-dmn-sim__expected-caret">${expanded ? '▾' : '▸'}</span>Esperado <span class="epx-dmn-sim__expected-hint">(opcional — dejalo vacío para ignorar)</span>`;
    header.addEventListener('click', () => {
      scenario._expectedOpen = !scenario._expectedOpen;
      this._redraw();
    });
    wrap.appendChild(header);

    if (!expanded) return wrap;

    const body = document.createElement('div');
    body.className = 'epx-dmn-sim__expected-body';
    decisions.forEach((el) => body.appendChild(this._buildExpectedRow(scenario, el)));
    wrap.appendChild(body);

    return wrap;
  }

  _hasExpectedSet(scenario, decisionEl) {
    const stored = scenario.expected && scenario.expected[decisionEl.id];
    if (!stored) return false;
    if (stored.mode === 'scalar') return stored.value != null && stored.value !== '';
    if (stored.mode === 'object') {
      const v = stored.values || {};
      return Object.keys(v).some((k) => v[k] !== '' && v[k] != null);
    }
    if (stored.mode === 'feel') return !!(stored.text && stored.text.trim());
    return false;
  }

  _ensureExpectedStorage(scenario, decisionEl, shape) {
    if (!scenario.expected) scenario.expected = {};
    const existing = scenario.expected[decisionEl.id];
    if (existing && existing.mode === shape.kind) return existing;
    // Shape changed (user swapped logic type or hit policy) — reset to fresh
    // storage matching the current shape. Values can't be migrated safely.
    const fresh = shape.kind === 'scalar'
      ? { mode: 'scalar', value: '' }
      : shape.kind === 'object'
        ? { mode: 'object', values: {} }
        : { mode: 'feel', text: '' };
    scenario.expected[decisionEl.id] = fresh;
    return fresh;
  }

  _buildExpectedRow(scenario, decisionEl) {
    const row = document.createElement('div');
    row.className = 'epx-dmn-sim__expected-row';

    const label = document.createElement('div');
    label.className = 'epx-dmn-sim__expected-label';
    label.textContent = decisionEl.name || decisionEl.id;
    row.appendChild(label);

    const shape = decisionShape(decisionEl);
    const store = this._ensureExpectedStorage(scenario, decisionEl, shape);

    if (shape.kind === 'scalar') {
      row.appendChild(this._buildTypedField(
        shape.typeRef,
        store.value ?? '',
        (v) => {
          store.value = v;
          this._saveScenarios();
          this._scheduleLiveEval();
        },
      ));
    } else if (shape.kind === 'object') {
      const grid = document.createElement('div');
      grid.className = 'epx-dmn-sim__expected-grid';
      shape.columns.forEach((col) => {
        const sub = document.createElement('div');
        sub.className = 'epx-dmn-sim__expected-sub';
        const subLbl = document.createElement('div');
        subLbl.className = 'epx-dmn-sim__expected-sublabel';
        const ty = document.createElement('span');
        ty.className = 'epx-dmn-sim__input-type';
        ty.textContent = col.typeRef;
        subLbl.textContent = col.name + ' ';
        subLbl.appendChild(ty);
        sub.appendChild(subLbl);
        const vals = store.values || (store.values = {});
        sub.appendChild(this._buildTypedField(
          col.typeRef,
          vals[col.name] ?? '',
          (v) => {
            vals[col.name] = v;
            this._saveScenarios();
            this._scheduleLiveEval();
          },
        ));
        grid.appendChild(sub);
      });
      row.appendChild(grid);
    } else {
      // FEEL free-text fallback (lists, complex, non-table logic)
      const ta = document.createElement('textarea');
      ta.className = 'epx-dmn-sim__feel-input';
      ta.rows = 2;
      ta.placeholder = 'FEEL — ej. [1,2,3]  o  {key:"val"}';
      ta.value = store.text || '';
      ta.addEventListener('input', () => {
        store.text = ta.value;
        this._saveScenarios();
        this._scheduleLiveEval();
      });
      row.appendChild(ta);
    }

    return row;
  }

  _buildTypedField(typeRef, value, onChange) {
    const input = document.createElement('input');
    input.className = 'epx-dmn-sim__input';
    input.type = (typeRef === 'number' || typeRef === 'integer' || typeRef === 'double') ? 'number' : 'text';
    input.value = value ?? '';
    input.placeholder = placeholderFor(typeRef);
    input.addEventListener('input', () => onChange(input.value));
    return input;
  }

  _buildInputRow(el, scenario) {
    const row = document.createElement('div');
    row.className = 'epx-dmn-sim__input-row';

    const key = this._inputKey(el);
    const typeRef = this._inputTypeRef(el);

    const lbl = document.createElement('label');
    lbl.className = 'epx-dmn-sim__input-label';
    lbl.textContent = key;
    const ty = document.createElement('span');
    ty.className = 'epx-dmn-sim__input-type';
    ty.textContent = typeRef;
    lbl.appendChild(ty);
    row.appendChild(lbl);

    const input = document.createElement('input');
    input.className = 'epx-dmn-sim__input';
    input.type = (typeRef === 'number' || typeRef === 'integer' || typeRef === 'double') ? 'number' : 'text';
    input.value = scenario.inputs[key] ?? '';
    input.placeholder = placeholderFor(typeRef);
    input.addEventListener('input', () => {
      scenario.inputs[key] = input.value;
      this._saveScenarios();
      this._scheduleLiveEval();
    });
    row.appendChild(input);

    return row;
  }

  // ── UI: results column ──────────────────────────────────────────────

  _buildResultsColumn() {
    const col = document.createElement('div');
    col.className = 'epx-dmn-sim__col epx-dmn-sim__col-results';

    const head = document.createElement('div');
    head.className = 'epx-dmn-sim__col-head';
    head.textContent = 'Resultados';
    col.appendChild(head);

    const strip = document.createElement('div');
    strip.className = 'epx-dmn-sim__results-strip';
    strip.dataset.role = 'results-strip';
    col.appendChild(strip);

    return col;
  }

  _renderResults() {
    const strip = this._container.querySelector('[data-role="results-strip"]');
    if (!strip) return;
    strip.innerHTML = '';

    // Focus pill (when user clicked a Decision on the DRD).
    if (this._focusedDecisionId) {
      const model = this._getModel();
      const decEl = model && (model.drgElements || []).find((e) => e.id === this._focusedDecisionId);
      const pill = document.createElement('div');
      pill.className = 'epx-dmn-sim__focus-pill';
      pill.innerHTML = `Filtrado a <strong>${escapeHtml((decEl && decEl.name) || this._focusedDecisionId)}</strong> `;
      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      clearBtn.className = 'epx-dmn-sim__focus-clear';
      clearBtn.textContent = '✕';
      clearBtn.title = 'Limpiar filtro';
      clearBtn.addEventListener('click', () => {
        this._focusedDecisionId = null;
        this._renderResults();
      });
      pill.appendChild(clearBtn);
      strip.appendChild(pill);
    }

    const ids = Array.from(this._selectedIds).slice(0, MAX_BATCH);
    if (!ids.length && this._activeId) ids.push(this._activeId);

    if (!ids.length) {
      strip.appendChild(this._muted('Marcá al menos un escenario y correlo.'));
      return;
    }

    ids.forEach((id) => {
      const scenario = this._scenarios.find((s) => s.id === id);
      if (!scenario) return;
      const res = this._resultsById.get(id);
      strip.appendChild(this._buildResultCard(scenario, res));
    });
  }

  _buildResultCard(scenario, res) {
    const card = document.createElement('div');
    card.className = 'epx-dmn-sim__result-card';

    const head = document.createElement('div');
    head.className = 'epx-dmn-sim__result-head';

    const icon = document.createElement('span');
    icon.className = 'epx-dmn-sim__result-icon';
    const name = document.createElement('span');
    name.className = 'epx-dmn-sim__result-name';
    name.textContent = scenario.name;

    const expand = document.createElement('button');
    expand.type = 'button';
    expand.className = 'epx-dmn-sim__result-expand';
    const expanded = this._expandedIds.has(scenario.id);
    expand.textContent = expanded ? 'Menos' : 'Detalle';
    expand.addEventListener('click', () => {
      if (expanded) this._expandedIds.delete(scenario.id);
      else this._expandedIds.add(scenario.id);
      this._renderResults();
    });

    head.appendChild(icon);
    head.appendChild(name);
    head.appendChild(expand);
    card.appendChild(head);

    if (!res) {
      icon.textContent = '—';
      icon.classList.add('epx-dmn-sim__result-icon--idle');
      card.appendChild(this._muted('Sin correr.'));
      return card;
    }

    if (!res.ok) {
      icon.textContent = '✗';
      icon.classList.add('epx-dmn-sim__result-icon--fail');
      const msg = document.createElement('div');
      msg.className = 'epx-dmn-sim__result-error';
      msg.textContent = res.message || 'Error al evaluar.';
      card.appendChild(msg);
      return card;
    }

    const { results, trace, warnings } = res.result;
    const assertions = res.assertions || { total: 0, pass: 0, fail: 0, errors: 0, perDecision: {} };
    const hasWarn = warnings && warnings.length > 0;
    const hasExpected = assertions.total > 0;

    if (hasWarn) {
      icon.textContent = '✗';
      icon.classList.add('epx-dmn-sim__result-icon--fail');
    } else if (hasExpected) {
      const allGreen = assertions.fail === 0 && assertions.errors === 0;
      icon.textContent = allGreen ? '✓' : '✗';
      icon.classList.add(allGreen ? 'epx-dmn-sim__result-icon--ok' : 'epx-dmn-sim__result-icon--fail');
    } else {
      icon.textContent = '✓';
      icon.classList.add('epx-dmn-sim__result-icon--ok');
    }

    if (hasExpected) {
      const summary = document.createElement('div');
      summary.className = 'epx-dmn-sim__assert-summary';
      const failN = assertions.fail + assertions.errors;
      if (failN === 0) {
        summary.classList.add('epx-dmn-sim__assert-summary--pass');
        summary.textContent = `${assertions.pass}/${assertions.total} expected passed`;
      } else {
        summary.classList.add('epx-dmn-sim__assert-summary--fail');
        summary.textContent = `${failN}/${assertions.total} expected failed`;
      }
      card.appendChild(summary);
    }

    const decisions = this._decisionElements();
    const visibleDecisions = this._focusedDecisionId
      ? decisions.filter((el) => el.id === this._focusedDecisionId)
      : decisions;
    visibleDecisions.forEach((el) => {
      const entry = trace.find((t) => t.id === el.id);
      const assertion = assertions.perDecision[el.id] || null;
      const block = document.createElement('div');
      block.className = 'epx-dmn-sim__dec';
      if (assertion) {
        if (assertion.status === 'pass') block.classList.add('epx-dmn-sim__dec--pass');
        else if (assertion.status === 'fail') block.classList.add('epx-dmn-sim__dec--fail');
        else if (assertion.status === 'error') block.classList.add('epx-dmn-sim__dec--fail');
      }

      const titleRow = document.createElement('div');
      titleRow.className = 'epx-dmn-sim__dec-title-row';
      const title = document.createElement('div');
      title.className = 'epx-dmn-sim__dec-title';
      title.textContent = el.name || el.id;
      titleRow.appendChild(title);
      if (assertion && assertion.status !== 'none') {
        const badge = document.createElement('span');
        badge.className = 'epx-dmn-sim__assert-badge';
        if (assertion.status === 'pass') {
          badge.classList.add('epx-dmn-sim__assert-badge--pass');
          badge.textContent = '✓ esperado';
        } else if (assertion.status === 'fail') {
          badge.classList.add('epx-dmn-sim__assert-badge--fail');
          badge.textContent = '✗ esperado';
        } else if (assertion.status === 'error') {
          badge.classList.add('epx-dmn-sim__assert-badge--fail');
          badge.textContent = '⚠ FEEL inválido';
        }
        titleRow.appendChild(badge);
      }
      block.appendChild(titleRow);

      if (entry && entry.error) {
        block.classList.add('epx-dmn-sim__dec--error');
        block.appendChild(this._buildErrorBreadcrumb(el, entry));
      } else {
        const value = document.createElement('pre');
        value.className = 'epx-dmn-sim__dec-value';
        value.textContent = stringify(results[el.id]);
        block.appendChild(value);
        if (assertion && assertion.status === 'fail') {
          const diff = document.createElement('div');
          diff.className = 'epx-dmn-sim__dec-diff';
          const exp = document.createElement('div');
          exp.className = 'epx-dmn-sim__dec-diff-line';
          exp.innerHTML = `<span class="epx-dmn-sim__dec-diff-tag">esperado</span> <code>${escapeHtml(stringify(assertion.expected))}</code>`;
          diff.appendChild(exp);
          block.appendChild(diff);
        } else if (assertion && assertion.status === 'error') {
          const err = document.createElement('div');
          err.className = 'epx-dmn-sim__dec-diff';
          err.innerHTML = `<span class="epx-dmn-sim__dec-diff-tag epx-dmn-sim__dec-diff-tag--err">expected FEEL</span> <code>${escapeHtml(assertion.parseError)}</code>`;
          block.appendChild(err);
        }
        if (entry && Array.isArray(entry.matchedRules) && entry.matchedRules.length) {
          const m = document.createElement('div');
          m.className = 'epx-dmn-sim__dec-meta';
          m.textContent = `matched: ${entry.matchedRules.length} rule(s)`;
          block.appendChild(m);
        }
      }
      card.appendChild(block);
    });

    if (expanded) {
      const details = document.createElement('details');
      details.className = 'epx-dmn-sim__trace';
      details.open = true;
      const summary = document.createElement('summary');
      summary.textContent = `Trace (${trace.length})`;
      details.appendChild(summary);
      const pre = document.createElement('pre');
      pre.textContent = trace.map((t) => formatTraceLine(t)).join('\n');
      details.appendChild(pre);
      card.appendChild(details);
    }

    return card;
  }

  _buildErrorBreadcrumb(decisionEl, entry) {
    const wrap = document.createElement('div');
    wrap.className = 'epx-dmn-sim__crumb';

    const ctx = entry.errorContext || {};
    const parts = [`Decision '${decisionEl.name || decisionEl.id}'`];

    if (ctx.kind === 'hitPolicy') {
      const rules = (ctx.conflictRuleIdxs || []).map((i) => `#${i + 1}`).join(', ');
      parts.push(`${ctx.policy}: conflicto ${rules ? 'entre ' + rules : ''}`);
    } else if (typeof ctx.ruleIdx === 'number') {
      const colLabel = ctx.kind === 'outputEntry'
        ? `output col ${Number(ctx.outputIdx ?? 0) + 1}`
        : `input col ${Number(ctx.inputIdx ?? 0) + 1}`;
      parts.push(`Rule #${ctx.ruleIdx + 1} › ${colLabel}`);
    } else if (ctx.kind === 'inputExpression') {
      parts.push(`Input expression col ${Number(ctx.inputIdx ?? 0) + 1}`);
    }

    const label = document.createElement('div');
    label.className = 'epx-dmn-sim__crumb-text';
    label.textContent = parts.join(' › ');
    wrap.appendChild(label);

    const msg = document.createElement('div');
    msg.className = 'epx-dmn-sim__crumb-msg';
    msg.textContent = entry.error;
    wrap.appendChild(msg);

    const jump = document.createElement('button');
    jump.type = 'button';
    jump.className = 'epx-dmn-sim__btn epx-dmn-sim__btn--link';
    jump.textContent = 'Ir a la celda';
    jump.addEventListener('click', () => this._jumpToFailure(decisionEl, entry));
    wrap.appendChild(jump);

    return wrap;
  }

  _jumpToFailure(decisionEl, entry) {
    const ctx = entry.errorContext || {};
    if (this._onRevealCell) {
      this._onRevealCell({
        decisionId: decisionEl.id,
        ruleId: ctx.ruleId || null,
        ruleIdx: typeof ctx.ruleIdx === 'number' ? ctx.ruleIdx : null,
        colIdx: ctx.kind === 'outputEntry' ? ctx.outputIdx : ctx.inputIdx,
        kind: ctx.kind || null,
        conflictRuleIdxs: ctx.conflictRuleIdxs || null,
      });
    }
  }

  _muted(text) {
    const d = document.createElement('div');
    d.className = 'epx-dmn-sim__muted';
    d.textContent = text;
    return d;
  }
}

function placeholderFor(typeRef) {
  if (typeRef === 'number' || typeRef === 'integer' || typeRef === 'double') return '0';
  if (typeRef === 'boolean') return 'true / false';
  return 'texto';
}

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatTraceLine(t) {
  if (t.error) return `[${t.kind}] ${t.id} → ERROR: ${t.error}`;
  if (t.kind === 'decision') return `[decision] ${t.id} = ${stringify(t.value)}`;
  if (t.kind === 'inputData') return `[input]    ${t.id} = ${stringify(t.value)}`;
  return `[${t.kind}] ${t.id}`;
}
